export interface CameraOption {
  deviceId: string;
  label: string;
  category: 'front' | 'rear';
}

export interface FrameSample {
  brightness: number;
  obstructed: boolean;
  overexposed: boolean;
  pixels: Uint8ClampedArray;
}

export function cameraOptions(devices: MediaDeviceInfo[], activeDeviceId?: string, activeFacingMode?: string): CameraOption[] {
  const cameras = devices.filter((device) => device.kind === 'videoinput').map((device, index) => ({
    deviceId: device.deviceId,
    source: device.label.trim(),
    index
  }));
  const front = cameras.filter((camera) => /front|user|facetime|selfie/i.test(camera.source));
  const rear = cameras.filter((camera) => /rear|back|environment|world|wide|tele|macro|depth|lidar/i.test(camera.source));
  const knownIds = new Set([...front, ...rear].map((camera) => camera.deviceId));
  const unknown = cameras.filter((camera) => !knownIds.has(camera.deviceId));
  type CameraCandidate = (typeof cameras)[number];
  let frontChoice: CameraCandidate | undefined = front[0];
  let rearChoice: CameraCandidate | undefined = [...rear].sort((a, b) => rearCameraScore(b.source) - rearCameraScore(a.source) || a.index - b.index)[0];

  if (!frontChoice && !rearChoice && cameras.length) {
    const active = cameras.find((camera) => camera.deviceId === activeDeviceId);
    if (cameras.length === 1) {
      if (activeFacingMode === 'user') frontChoice = active ?? cameras[0];
      else rearChoice = active ?? cameras[0];
    } else if (active) {
      if (activeFacingMode === 'user') {
        frontChoice = active;
        rearChoice = cameras.find((camera) => camera.deviceId !== active.deviceId);
      } else {
        rearChoice = active;
        frontChoice = cameras.find((camera) => camera.deviceId !== active.deviceId);
      }
    } else {
      frontChoice = cameras[0];
      rearChoice = cameras.at(-1);
    }
  } else {
    if (!frontChoice) frontChoice = unknown[0];
    if (!rearChoice) rearChoice = unknown.find((camera) => camera.deviceId !== frontChoice?.deviceId);
  }

  return [
    frontChoice ? { deviceId: frontChoice.deviceId, label: 'Front Camera', category: 'front' as const } : undefined,
    rearChoice ? { deviceId: rearChoice.deviceId, label: 'Rear Camera', category: 'rear' as const } : undefined
  ].filter((camera): camera is CameraOption => Boolean(camera));
}

function rearCameraScore(label: string): number {
  const auxiliary = /ultra|ultrawide|ultra-wide|tele|telephoto|macro|depth|lidar/i.test(label);
  const primary = /(^|\s)(rear|back|environment|world)(\s|$)/i.test(label);
  return (primary ? 20 : 0) - (auxiliary ? 100 : 0);
}

export function preferredCamera(options: CameraOption[], previousDeviceId?: string): string {
  if (previousDeviceId && options.some((option) => option.deviceId === previousDeviceId)) return previousDeviceId;
  return options.find((option) => option.category === 'rear')?.deviceId ?? options[0]?.deviceId ?? '';
}

export function selectedCameraConstraints(deviceId?: string): MediaTrackConstraints {
  return deviceId
    ? { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } }
    : { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } };
}

export function friendlyCameraError(error: unknown): string {
  const name = error instanceof DOMException || error instanceof Error ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'Camera permission was denied. Allow camera access in browser settings, then retry.';
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') return 'No camera was found on this device.';
  if (name === 'NotReadableError' || name === 'TrackStartError') return 'The camera is already in use or unavailable. Close other camera apps, then retry.';
  if (name === 'OverconstrainedError') return 'The selected camera is no longer available. Choose another camera and retry.';
  return 'The camera could not start. Check browser permissions and try again.';
}

export function sampleVideoFrame(video: HTMLVideoElement, width = 32, height = 18): FrameSample | undefined {
  if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) return undefined;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return undefined;
  context.drawImage(video, 0, 0, width, height);
  const pixels = context.getImageData(0, 0, width, height).data;
  let total = 0;
  for (let index = 0; index < pixels.length; index += 4) total += (pixels[index] + pixels[index + 1] + pixels[index + 2]) / 3;
  const brightness = total / (pixels.length / 4);
  return { brightness, obstructed: brightness < 12, overexposed: brightness > 245, pixels };
}

export function averageFrameDifference(previous: Uint8ClampedArray | undefined, current: Uint8ClampedArray): number {
  if (!previous || previous.length !== current.length) return Number.POSITIVE_INFINITY;
  let total = 0;
  for (let index = 0; index < current.length; index += 4) {
    total += Math.abs(current[index] - previous[index]);
    total += Math.abs(current[index + 1] - previous[index + 1]);
    total += Math.abs(current[index + 2] - previous[index + 2]);
  }
  return total / ((current.length / 4) * 3);
}
